<template>
    <hts-drawer
        v-model="visible"
        color="main"
        size="large"
        :title="$t('labels.nodeConnection')">
        <div v-loading="!visible">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.adminCP.node_add)"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    type="submit"
                    @click="handleAddNodeConnection">
                    {{ $t('labels.addNodeConnection') }}
                </hts-form-button>
            </div>

            <hts-data-table
                ref="dataTable"
                class="ml-2"
                :headers="headers"
                :server="server"
                :use-archive="useNodeConnectionArchive"
                :use-restore="useNodeConnectionRestore"
                use-selection
                :pagination="true"
                store-name="nodeDataTable"
                store-key="NodeConnection" />

            <add-node-connection
                ref="addNodeConnection"
                @refreshTables="refreshTables" />
            <detail-node-connection
                ref="detailNodeConnection"
                @refreshTables="refreshTables" />
            <edit-node-connection
                ref="editNodeConnection"
                @refreshTables="refreshTables" />

            <hts-modal
                :show="showPopupDelete"
                :title="$t('labels.deleteNodeConnection')"
                type="confirm"
                :content="$t('labels.deleteNodeConnectionConfirm')"
                @close="closePopupDelete"
                @confirm="handleDelete" />
        </div>
    </hts-drawer>
</template>

<script setup lang="ts">
import AddNodeConnection from './add.vue'
import DetailNodeConnection from './detail.vue'
import EditNodeConnection from './edit.vue'
import BusinessName from './tables/business-name.vue'
import NodeName from './tables/node-name.vue'
import TaskName from './tables/task-name.vue'

import layoutPermissions from '~/data/permissions/layout-permissions'
import Actions from '~/pages/trucker-[id]/vehicle/ignore/actions.vue'
const visible = ref(false)
const addNodeConnection = ref()
const detailNodeConnection = ref()
const editNodeConnection = ref()
const nodeId = ref('')
const connectionId = ref('')
const server = ref({ ...$urlAdmin.node.connection.getList })
const dataTable = ref()
const store = $store()
const showPopupDelete = ref(false)

const open = (id: string) => {
  nodeId.value = id
  visible.value = true
  server.value.url = `${$urlAdmin.node.connection.getList.url}${id}`
  refreshTables()
}

const refreshTables = () => {
  if (dataTable.value) {
    dataTable.value.refresh()
  }
}

const handleAddNodeConnection = () => {
  if (addNodeConnection.value && nodeId.value) {
    addNodeConnection.value.open(nodeId.value)
  }
}

const handleActionViewDetail = (connection: any) => {
  if (detailNodeConnection.value && nodeId.value) {
    detailNodeConnection.value.open(nodeId.value, String(connection._id))
  }
}

const handleActionEdit = (connection: any) => {
  if (editNodeConnection.value && nodeId.value) {
    editNodeConnection.value.open(nodeId.value, String(connection._id))
  }
}

const handleActionDelete = (connection: any) => {
  if (!connection) {
    return null
  }
  showPopupDelete.value = true
  connectionId.value = connection._id
}

const closePopupDelete = () => {
  connectionId.value = ''
  showPopupDelete.value = false
}

const handleDelete = () => {
  const api = $urlAdmin.node.connection.delete
  $api(api, { body: { ids: [connectionId.value] } })
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        closePopupDelete()
        process.client && $toast().success($t('labels.deleteSuccess'))
        refreshTables()
      }
    })
    .catch((error: any) => error)
}

const headers = [
  {
    field: 'startNode',
    headerName: $t('labels.startNode'),
    valueGetter: (params: any) => {
      try {
        const nodeTypeName = params?.data?.startNode?.nodeType?.name || ''
        const locationName = params?.data?.startNode?.location?.locationName || ''
        if (nodeTypeName || locationName) {
          return `${nodeTypeName} - ${locationName}`
        }
        return ''
      } catch (e) {
        return ''
      }
    }
  },
  {
    field: 'businesses',
    headerName: $t('labels.businessConnection'),
    cellRenderer: BusinessName,
    autoHeight: true
  },
  {
    field: 'businesses',
    headerName: $t('labels.taskConnection'),
    cellRenderer: TaskName,
    autoHeight: true
  },
  {
    field: 'nodeConnection',
    headerName: $t('labels.nodeConnection'),
    cellRenderer: NodeName,
    autoHeight: true
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'nodeDataTable',
      storeName: 'NodeConnection',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          isAll: true,
          click: handleActionViewDetail
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_edit),
          click: handleActionEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_delete),
          isArchive: true,
          click: handleActionDelete
        }
      ]
    }
  }
]

const useNodeConnectionRestore = computed(() => {
  return { api: $urlAdmin.node.connection.restore, permissions: checkPermission(layoutPermissions.adminCP.node_archive) }
})

const useNodeConnectionArchive = computed(() => {
  return { api: $urlAdmin.node.connection.archive, permissions: checkPermission(layoutPermissions.adminCP.node_archive) }
})

defineExpose({
  open
})
</script>
